/**
 * Sentry error tracking for the dashboard.
 *
 * Gracefully no-ops when VITE_SENTRY_DSN is not set (local dev).
 * Must be imported before React renders so Sentry captures all errors.
 */
import * as Sentry from '@sentry/react';

import { SCANNER_NOISE_PATTERN } from './scannerNoise';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE || 'development',

    // Email-security link scanners detonating emailed links throw
    // "Object Not Found Matching Id..." from injected code. Third-party
    // noise, never ours: see lib/scannerNoise.ts + sentry-javascript#3440.
    ignoreErrors: [SCANNER_NOISE_PATTERN],

    // Sample 100% of errors (low volume), 10% of performance traces.
    sampleRate: 1.0,
    tracesSampleRate: 0.1,

    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    initialScope: {
      tags: {
        surface: 'dashboard',
      },
    },

    beforeSend(event) {
      if (event.user) {
        delete event.user.ip_address;
      }
      return event;
    },
  });
}

export { Sentry };
export const sentryEnabled = Boolean(dsn);

/**
 * Identify the current user in Sentry (call after Clerk auth resolves).
 */
export function setSentryUser(user: { id: string; email?: string | null }) {
  if (!sentryEnabled) return;
  Sentry.setUser({ id: user.id, email: user.email ?? undefined });
}

export function clearSentryUser() {
  if (!sentryEnabled) return;
  Sentry.setUser(null);
}
